/**
 * @fileoverview Utility functions for optimization job management
 * 
 * Features:
 * - Memory-efficient job sanitization
 * - Completed job cleanup logic
 */

import type { OptimizationJob } from '../services/optimization/OptimizationEventService';

export const RECENT_COMPLETED_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Sanitize job data to remove large objects and prevent memory leaks
 * for completed or failed jobs
 */
export const sanitizeJobForMemory = (job: OptimizationJob): OptimizationJob => {
  if (job.status === 'completed' || job.status === 'failed') {
    // Keep only essential data for completed/failed jobs to prevent memory leaks
    return {
      ...job,
      // Keep bestResult but limit its size
      bestResult: job.bestResult ? {
        parameters: job.bestResult.parameters || {},
        score: job.bestResult.score || 0,
        metrics: job.bestResult.metrics || {
          totalReturnPercent: 0,
          totalReturn: 0,
          sharpeRatio: 0,
          winRate: 0,
          maxDrawdown: 0,
          maxDrawdownPercent: 0,
          tradeCount: 0,
          profitableTrades: 0,
          unprofitableTrades: 0,
          avgReturn: 0,
          avgWin: 0,
          avgLoss: 0,
          profitFactor: 0,
          expectancy: 0,
          totalTrades: 0
        }
      } : undefined
    };
  }
  return job;
};

/**
 * Trigger garbage collection if available (mainly for debugging)
 */
export const triggerGarbageCollection = () => {
  if ((globalThis as any).gc) {
    try {
      (globalThis as any).gc();
    } catch (e) {
      // gc() may not be available
    }
  }
};
